import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Bot, Clock, ChevronDown, ChevronUp } from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

const TYPE_STYLES = {
  STAKE_QUOTE: 'bg-blue-500/20 text-blue-400',
  REWARD_OFFER: 'bg-lime-500/20 text-lime-400',
  SUPPRESS: 'bg-pink-500/20 text-pink-400',
  REVIEW_REQUIRED: 'bg-orange-500/20 text-orange-400',
};

export default function DecisionsPage() {
  const { api } = useAuth();
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/decisions?limit=30');
      setDecisions(res.data);
    } catch (err) {
      console.error('Decisions fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="decisions-page" className="space-y-6">
      <motion.div {...fadeUp}>
        <h1 className="font-barlow text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
          <Bot size={28} className="text-blue-400" />
          AI Decisions
        </h1>
        <p className="text-slate-400 text-sm font-manrope mt-0.5">
          Transparent, auditable agent decisions that affect your account
        </p>
      </motion.div>

      {/* Decision Timeline */}
      <div className="space-y-3">
        {decisions.map((dec, i) => {
          const isExpanded = expandedId === dec.id;
          return (
            <motion.div
              key={dec.id || i}
              {...fadeUp}
              transition={{ delay: i * 0.03, duration: 0.3 }}
              className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden hover:border-blue-500/15 transition-all"
              data-testid={`decision-item-${i}`}
            >
              <button
                onClick={() => setExpandedId(isExpanded ? null : dec.id)}
                className="w-full flex items-center gap-3 p-4 text-left"
                data-testid={`decision-toggle-${i}`}
              >
                {/* Timeline dot */}
                <div className="relative flex-shrink-0">
                  <div className={`w-3 h-3 rounded-full ${
                    dec.decision_type === 'STAKE_QUOTE' ? 'bg-blue-400' :
                    dec.decision_type === 'REWARD_OFFER' ? 'bg-lime-400' :
                    dec.decision_type === 'SUPPRESS' ? 'bg-pink-400' :
                    'bg-orange-400'
                  }`} />
                  {dec.status === 'executed' && (
                    <div className={`absolute inset-0 rounded-full animate-ping opacity-30 ${
                      dec.decision_type === 'STAKE_QUOTE' ? 'bg-blue-400' :
                      dec.decision_type === 'REWARD_OFFER' ? 'bg-lime-400' :
                      dec.decision_type === 'SUPPRESS' ? 'bg-pink-400' :
                      'bg-orange-400'
                    }`} />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold ${
                      TYPE_STYLES[dec.decision_type] || 'bg-slate-500/20 text-slate-400'
                    }`}>{dec.decision_type?.replace('_', ' ')}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      dec.status === 'executed' ? 'bg-lime-500/10 text-lime-400' :
                      dec.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' :
                      'bg-slate-500/10 text-slate-400'
                    }`}>{dec.status}</span>
                    <span className="text-[10px] text-slate-500 font-mono">{dec.agent_name}</span>
                  </div>
                  <p className="text-slate-300 text-sm truncate">{dec.explanation_user}</p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="text-right hidden md:block">
                    <p className="text-xs text-slate-500 font-mono">{new Date(dec.created_at).toLocaleDateString()}</p>
                    <p className="text-[10px] text-slate-600">{new Date(dec.created_at).toLocaleTimeString()}</p>
                  </div>
                  {isExpanded ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
                </div>
              </button>

              {/* Expanded Details */}
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="px-4 pb-4 border-t border-white/5"
                >
                  <div className="pt-3 grid md:grid-cols-2 gap-4 text-xs">
                    <div>
                      <p className="text-slate-400 uppercase tracking-wider mb-1 font-bold">User Explanation</p>
                      <p className="text-slate-200 bg-white/5 rounded-lg p-3">{dec.explanation_user}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 uppercase tracking-wider mb-1 font-bold">Internal Reasoning</p>
                      <p className="text-slate-200 bg-white/5 rounded-lg p-3 font-mono text-[11px]">{dec.explanation_internal}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 uppercase tracking-wider mb-1 font-bold">Reason Codes</p>
                      <div className="flex gap-1.5 flex-wrap">
                        {(dec.reason_codes || []).map((rc, j) => (
                          <span key={j} className="px-2 py-1 rounded bg-blue-500/10 text-blue-300 font-mono">{rc}</span>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Policy</span>
                        <span className="text-white font-mono">{dec.policy_id} v{dec.policy_version}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Model</span>
                        <span className="text-white font-mono">{dec.model_version}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Decision ID</span>
                        <span className="text-white font-mono text-[10px]">{dec.id?.substring(0, 16)}...</span>
                      </div>
                      {dec.corridor_bounds && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Corridor</span>
                          <span className="text-white font-mono">{dec.corridor_bounds.min} - {dec.corridor_bounds.max}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>

      {decisions.length === 0 && (
        <div className="text-center py-16 text-slate-500">
          <Bot size={48} className="mx-auto mb-3 opacity-30" />
          <p>No agent decisions yet</p>
        </div>
      )}
    </div>
  );
}
